import React from 'react';
import MuiTooltip from '@mui/material/Tooltip';

/**
 * Standardized display for null/missing values.
 *
 * Maps semantic NULL categories to 3 user-facing labels:
 *   - "N/A"         ← Not Applicable (NA), Structurally Absent (SA)
 *   - "No Data"     ← Not Reported (NR), Not Yet Available (NYA)
 *   - "Not in SERB" ← Not In Source System (NISS), salary domain only
 *
 * @param {string} [category] - Semantic category: "NA", "SA", "NR", "NYA", "NISS"
 * @param {string} [tooltip]  - Custom tooltip override
 * @returns {JSX.Element}
 */

const CATEGORY_CONFIG = {
  NA:   { label: 'N/A',          tooltip: 'This metric does not apply to this district' },
  SA:   { label: 'N/A',          tooltip: 'This data element does not exist for this record' },
  NR:   { label: 'No Data',      tooltip: 'Data not yet reported for this period' },
  NYA:  { label: 'No Data',      tooltip: 'Data not yet available for this period' },
  NISS: { label: 'Not in SERB',  tooltip: 'This district does not have a SERB contract on file' },
};

const DEFAULT_CONFIG = { label: 'No Data', tooltip: 'Data not available' };

export default function NullDisplay({ category, tooltip }) {
  const config = CATEGORY_CONFIG[category] || DEFAULT_CONFIG;
  const displayTooltip = tooltip || config.tooltip;

  return (
    <MuiTooltip title={displayTooltip} arrow placement="top">
      <span className="null-display">{config.label}</span>
    </MuiTooltip>
  );
}

/**
 * Returns the plain-text label for a null category.
 * Use in valueFormatters, map tooltips, and other non-JSX contexts.
 */
export function nullText(category) {
  return (CATEGORY_CONFIG[category] || DEFAULT_CONFIG).label;
}

/**
 * Default null text for contexts where category is unknown.
 * Convenience constant for simple string replacements.
 */
export const NULL_DISPLAY_TEXT = 'No Data';
export const NO_DATA_TEXT = 'No Data';
